/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import type React from 'react';
import { cn } from '@/lib/utils';
import { useAnalytics } from '@/hooks/use-analytics';
import { Link } from '@/components/link';

interface Suggestion {
    name: string;
    link: string;
    type: string;
    image?: string;
    parentCategoryName?: string;
}

interface SuggestionsProps {
    suggestions?: Suggestion[];
    searchPhrase?: string;
    closeAndNavigate?: (link: string) => void;
    className?: string;
}

const Suggestions: React.FC<SuggestionsProps> = ({ suggestions, searchPhrase, closeAndNavigate, className }) => {
    const analytics = useAnalytics();
    if (!suggestions || suggestions.length === 0) {
        return null;
    }

    const handleClick = (suggestion: Suggestion) => {
        void analytics.trackClickSearchSuggestion({
            searchInputText: searchPhrase || '',
            suggestion: suggestion.name,
        });
        if (closeAndNavigate) {
            closeAndNavigate(suggestion.link);
        }
    };

    return (
        <div data-testid="sf-suggestion" className={cn('space-y-0', className)}>
            <div className="flex flex-col gap-1">
                {suggestions.map((suggestion) => (
                    <Link
                        key={suggestion.link}
                        to={suggestion.link}
                        data-slot="suggestion"
                        onMouseDown={() => handleClick(suggestion)}
                        className="w-full flex justify-start py-0 text-header-menu-foreground hover:bg-transparent hover:text-header-menu-foreground/60 focus:bg-transparent focus:text-header-menu-foreground/60 transition-colors text-sm mt-0">
                        <div className="flex">
                            {/* suggestion image not needed but we keep the code for future reference */}
                            {/* {suggestion.image && (
                                <div className="w-10 h-8 mr-4 rounded-full bg-transparent flex justify-center overflow-hidden shrink-0">
                                    <DynamicImage
                                        src={`${toImageUrl({ src: suggestion.image, config })}[?sw={width}]`}
                                        alt={suggestion.name || t('suggestionImageAlt')}
                                        className="w-full h-full"
                                        imageProps={{
                                            className: 'w-full h-full object-cover rounded-full',
                                        }}
                                        loading="eager"
                                    />
                                </div>
                            )} */}
                            <div className="text-left">
                                <span className="text-base font-medium">{suggestion.name}</span>
                            </div>
                        </div>
                    </Link>
                ))}
            </div>
        </div>
    );
};

export default Suggestions;
